import {Role, RoleEnumFromString} from "./Role.ts";
import {UserBusinessFlagsService} from "../../lib/Services/UserBusinessFlagsService.ts";
import {BackendApi} from "../api.ts";
import {BusinessSettingsService} from "../../lib/Services/BusinessSettingsService.ts";

export type UserAccess = Array<{ business_id: string, business_name: string }>;


export interface rawApiResponse {
    id: string,
    first_name: string,
    last_name: string,
    email: string,
    role: string,
    businesses: string[],
    access: {
        business_id: string,
        business_name: string,
    }[],
    flags: object,
    business_settings: object,
    business_name: null | string,
    business: {
        partner_id: string | null
    },
}

export class User {
    constructor(
        readonly id: string,
        readonly firstName: string,
        readonly lastName: string,
        readonly email: string,
        readonly role: Role,
        readonly businesses: Array<string>,
        readonly access: UserAccess,
        public partnerId: string | null,
        private businessName: string | null = null,
        private token: string | null = null,
        private flagsService: UserBusinessFlagsService | null = null,
        private businessSettingsService: BusinessSettingsService | null = null,
    ) {
    }

    public static fromApiResponse(data: rawApiResponse, api: BackendApi): User {
        const user = new User(
            data.id,
            data.first_name,
            data.last_name,
            data.email,
            RoleEnumFromString(data.role),
            data.businesses,
            data.access || [],
            data.business.partner_id,
            data.business_name
        )

        const flag = new UserBusinessFlagsService(data.flags as Record<string, boolean>, api)
        const settings = new BusinessSettingsService(data.business_settings as Record<string, any>, api)

        return user.withToken(api.token).withBusinessFlagsService(flag).withBusinessSettingsService(settings)
    }

    public withToken(token: string): User {
        return new User(
            this.id,
            this.firstName,
            this.lastName,
            this.email,
            this.role,
            this.businesses,
            this.access,
            this.partnerId,
            this.businessName,
            token
        )
    }

    public withBusinessFlagsService(service: UserBusinessFlagsService): User {
        return new User(
            this.id,
            this.firstName,
            this.lastName,
            this.email,
            this.role,
            this.businesses,
            this.access,
            this.partnerId,
            this.businessName,
            this.token,
            service
        )
    }

    public withBusinessSettingsService(service: BusinessSettingsService): User {
        return new User(
            this.id,
            this.firstName,
            this.lastName,
            this.email,
            this.role,
            this.businesses,
            this.access,
            this.partnerId,
            this.businessName,
            this.token,
            this.flagsService,
            service
        )
    }

    get flags(): UserBusinessFlagsService {
        if (this.flagsService === null)
            throw new Error('.withBusinessFlagsService(...) has likely not been called, so the user object does not have a flags service')

        return this.flagsService
    }

    get businessSettings(): BusinessSettingsService {
        if (this.businessSettingsService === null)
            throw new Error('.withBusinessSettingsService(...) has likely not been called, so the user object does not have a flags service')

        return this.businessSettingsService
    }

    public getToken() {
        if (this.token === null)
            throw new Error('.withToken(...) has likely not been called, so the user object does not have a token')

        return this.token
    }

    public getFullName(): string {
        return `${this.firstName} ${this.lastName}`;
    }

    public getBusinessName(): string | null {
        return this.businessName;
    }

}
